import { useState } from "react";

import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { saveAs } from "file-saver";
import { AnimatePresence } from "framer-motion";
import { observer } from "mobx-react-lite";

import { RouterPresignedUrls } from "src/api/endpoints";
import { NodeTestRunResult } from "src/api/types";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { DropdownItem } from "src/design-system/Dropdown";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type Props = {
  results: NodeTestRunResult;
  nodeName: string;
  workspaceUrl: string;
};

export const DownloadDataButton: React.FC<Props> = observer(
  ({ results, nodeName, workspaceUrl }) => {
    const [jsonDownloadInProgress, setJsonDownloadInProgress] = useState(false);
    const [csvDownloadInProgress, setCsvDownloadInProgress] = useState(false);

    const handleLinkClick = async (
      format: "json" | "csv",
      onComplete: () => void,
    ) => {
      if (format === "json") {
        setJsonDownloadInProgress(true);
      } else {
        setCsvDownloadInProgress(true);
      }
      const filename = `${nodeName}.${format}`;

      try {
        let fileUrl: string | null = null;
        let retries = 0;
        while (!fileUrl) {
          if (retries === 10) {
            throw Error("Number of retries was exceeded");
          }
          try {
            fileUrl = (
              await RouterPresignedUrls.post(
                workspaceUrl,
                results.test_run_id,
                {
                  type: "node_results",
                  format,
                  node_id: results.node_id,
                  run_id: results.test_run_id,
                  version_id: results.version_id,
                },
              )
            ).data.url;
          } catch {
            retries++;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
        saveAs(fileUrl, filename);
      } catch (err) {
        logger.error("Failed to download intermediate results", err);
        toastActions.failure({
          title:
            "Something went wrong when downloading the intermediate results, please try again",
        });
      } finally {
        if (format === "json") {
          setJsonDownloadInProgress(false);
        } else {
          setCsvDownloadInProgress(false);
        }
        onComplete();
      }
    };

    return (
      <Menu as="div" className="relative">
        {({ close }) => (
          <>
            <MenuButton
              as={IconButton}
              dataLoc="download-intermediate-data-icon"
              icon={faArrowDownToLine}
              size="xs"
            />
            <AnimatePresence initial={false}>
              <MenuItems
                anchor={{ to: "bottom start", gap: 6 }}
                as={MotionDropdownPanel}
              >
                <MenuItem
                  as={DropdownItem}
                  data-loc="json-download"
                  disabled={jsonDownloadInProgress}
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleLinkClick("json", close);
                  }}
                >
                  {jsonDownloadInProgress ? (
                    <SkeletonPlaceholder height="h-4" width="w-3/4" />
                  ) : (
                    "Download as JSON"
                  )}
                </MenuItem>
                <MenuItem
                  as={DropdownItem}
                  data-loc="csv-download"
                  disabled={csvDownloadInProgress}
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleLinkClick("csv", close);
                  }}
                >
                  {csvDownloadInProgress ? (
                    <SkeletonPlaceholder height="h-4" width="w-3/4" />
                  ) : (
                    "Download as CSV"
                  )}
                </MenuItem>
              </MenuItems>
            </AnimatePresence>
          </>
        )}
      </Menu>
    );
  },
);
